import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import _ from 'lodash';
import WizardStepContainer from '../WizardStepContainer';
import { WizardStylesContext } from '../WizardStylesContext';
import WizardStepButtons from '../WizardStepButtons';
import AssignmentList from '../../../pages/Dialog/Planning/AssignmentList';
import { getMarketPresences } from '../../../contexts/MarketPresencesContext/marketPresencesHelper';
import { MarketPresencesContext } from '../../../contexts/MarketPresencesContext/MarketPresencesContext';
import { addPlanningInvestible, stageChangeInvestible, updateInvestible } from '../../../api/investibles';
import { formInvestibleLink, navigate } from '../../../utils/marketIdPathFunctions';
import { useHistory } from 'react-router';
import { getInvestible, refreshInvestibles } from '../../../contexts/InvestibesContext/investiblesContextHelper';
import { InvestiblesContext } from '../../../contexts/InvestibesContext/InvestiblesContext';
import { useIntl } from 'react-intl';
import { CommentsContext } from '../../../contexts/CommentsContext/CommentsContext';
import { getMarketInfo } from '../../../utils/userFunctions';
import {
  getFullStage,
  getFurtherWorkStage,
  getInCurrentVotingStage,
  isFurtherWorkStage
} from '../../../contexts/MarketStagesContext/marketStagesContextHelper';
import { MarketStagesContext } from '../../../contexts/MarketStagesContext/MarketStagesContext';
import { onInvestibleStageChange } from '../../../utils/investibleFunctions';
import { createJobNameFromComments } from '../../../pages/Dialog/Planning/userUtils';

function JobAssignStep (props) {
  const { marketId, updateFormData = () => {}, formData = {}, onFinish, assigneeId, groupId, moveFromComments, roots } = props;
  const history = useHistory();
  const value = formData.wasSet ? (formData.assigned || []) : (assigneeId ? [assigneeId] : []);
  const validForm = !_.isEmpty(value);
  const intl = useIntl();
  const [presencesState, marketPresencesDispatch] = useContext(MarketPresencesContext);
  const presences = (getMarketPresences(presencesState, marketId) || []).filter(
    (presence) => presence.external_id !== marketId);
  const [investiblesState, investiblesDispatch] = useContext(InvestiblesContext);
  const [commentsState, commentsDispatch] = useContext(CommentsContext);
  const [marketStagesState] = useContext(MarketStagesContext);
  const classes = useContext(WizardStylesContext);
  const { investibleId } = formData;
  const inv = getInvestible(investiblesState, investibleId);
  const marketInfo = getMarketInfo(inv, marketId) || {};
  const { stage: stageId } = marketInfo;
  const fullCurrentStage = getFullStage(marketStagesState, marketId, stageId) || {};

  function onAssignmentChange(newAssignments){
    updateFormData({
      assigned: newAssignments,
      wasSet: true
    });
  }

  function createJob() {
    const name = createJobNameFromComments(roots, intl);
    // Coming from existing comments usually ready to assign - bugs are and voted questions or suggestion should be
    const addInfo = {
      name,
      groupId,
      marketId
    }
    if (validForm) {
      addInfo.assignments = value;
    }
    return addPlanningInvestible(addInfo)
      .then((inv) => {
        refreshInvestibles(investiblesDispatch, () => {}, [inv]);
        const { id: investibleId } = inv.investible;
        let link = formInvestibleLink(marketId, investibleId);
        // update the form data with the saved investible
        updateFormData({
          investibleId,
          link,
        });
        if (moveFromComments) {
          return moveFromComments(inv, formData, updateFormData);
        }
      })
  }

  function assignJob() {
    if (validForm) {
      const isBacklogAlready = isFurtherWorkStage(fullCurrentStage);
      if ((_.isEmpty(value) && !isBacklogAlready) || (!_.isEmpty(value) && isBacklogAlready)) {
        // if assignments changing from none to some or vice versa need to use stageChangeInvestible instead
        const fullMoveStage = isBacklogAlready ? getInCurrentVotingStage(marketStagesState, marketId) :
          getFurtherWorkStage(marketStagesState, marketId);
        const moveInfo = {
          marketId,
          investibleId,
          stageInfo: {
            assignments: value,
            current_stage_id: stageId,
            stage_id: fullMoveStage.id,
          },
        };
        return stageChangeInvestible(moveInfo)
          .then((newInv) => {
            onInvestibleStageChange(fullMoveStage.id, newInv, investibleId, marketId, commentsState,
              commentsDispatch, investiblesDispatch, () => {}, marketStagesState, undefined,
              fullCurrentStage, marketPresencesDispatch);
            return formData;
          });
      } else {
        const updateInfo = {
          marketId,
          investibleId,
          assignments: value,
        };
        return updateInvestible(updateInfo).then((fullInvestible) => {
          refreshInvestibles(investiblesDispatch, () => {}, [fullInvestible]);
          return formData;
        });
      }
    }
    return Promise.resolve(true);
  }

  return (
    <WizardStepContainer
      {...props}
      isLarge
    >
        <Typography className={classes.introText} variant="h6">
          Who should be working on the job?
        </Typography>
        <Typography className={classes.introSubText} variant="subtitle1">
          Not assigning sends to job backlog.
        </Typography>
        <AssignmentList
          fullMarketPresences={presences}
          previouslyAssigned={assigneeId ? [assigneeId] : undefined}
          onChange={onAssignmentChange}
          groupId={groupId}
          marketId={marketId}
          onlyThisGroup
        />
        <div className={classes.borderBottom}/>
        <WizardStepButtons
          {...props}
          finish={onFinish}
          validForm={validForm}
          showNext
          onNextSkipStep
          showTerminate
          onNext={investibleId ? assignJob : createJob}
          onOtherNext={investibleId ? assignJob : createJob}
          isFinal={false}
          showOtherNext
          otherNextLabel="addApproversLabel"
          onTerminate={() => navigate(history, formData.link)}
          terminateLabel="JobWizardGotoJob"
          nextLabel="JobWizardAssignJob"
        />
    </WizardStepContainer>
  )
}

JobAssignStep.propTypes = {
  updateFormData: PropTypes.func,
  formData: PropTypes.object,
}

export default JobAssignStep